import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { PublicSiteFooter, PublicSiteHeader } from "../components/PublicSiteChrome";
import { confirmAction } from "../components/ConfirmationModal";
import { getStoredAuthToken } from "../services/apiClient";
import { clearAuthSession, loginAccount, saveAuthSession } from "../services/authService";
import {
  cancelAccountDeletion,
  getAccountDeletionRequest,
  requestAccountDeletion,
} from "../services/adminDataService";

const dateText = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";
const allowedDeletionRoles = ["user", "driver"];
const deletionRoleOptions = [
  { value: "user", label: "Customer" },
  { value: "driver", label: "Driver" },
];
const roleFromLoginQuery = (search) => {
  const role = new URLSearchParams(search).get("login")?.toLowerCase();
  return allowedDeletionRoles.includes(role) ? role : "user";
};
const storedRole = () => {
  const directRole = localStorage.getItem("userRole");
  if (directRole) return directRole;
  try { return JSON.parse(localStorage.getItem("authUser") || "{}").role || ""; } catch { return ""; }
};

const SignInGate = ({ initialRole, onSignedIn }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(initialRole);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setRole(initialRole);
  }, [initialRole]);

  const submit = async (event) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      setError("Enter your email or phone number and password.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      const auth = await loginAccount({ email: email.trim(), password, role });
      if (!auth.token) throw new Error("Sign-in did not return an access token.");
      if (!allowedDeletionRoles.includes(auth.role)) {
        throw new Error("Account deletion is available only for customer and driver accounts.");
      }
      if (auth.role !== role) {
        throw new Error(`These credentials belong to a ${auth.role} account. Select that account type and try again.`);
      }
      saveAuthSession(auth);
      onSignedIn();
    } catch (loginError) {
      setError(loginError.message || "Unable to sign in.");
    } finally {
      setSaving(false);
    }
  };

  return <main className="deletion-page"><PublicSiteHeader active="deletion" /><section className="deletion-card deletion-signin-gate"><div className="deletion-card-top"><div className="deletion-icon"><i className="bi bi-lock" /></div><span className="deletion-badge">Protected account action</span></div><p className="legal-kicker">Account deletion</p><h1>Sign in to continue</h1><p>For your security, an account deletion request can only be created from a signed-in account. Choose the account type you want to manage, then sign in below.</p><form className="deletion-signin-form" onSubmit={submit}><fieldset className="deletion-role-options"><legend>Account type</legend><div>{deletionRoleOptions.map((option) => <label key={option.value}><input type="radio" name="deletion-account-role" value={option.value} checked={role === option.value} onChange={() => setRole(option.value)} disabled={saving} /> {option.label}</label>)}</div></fieldset><label>Email or phone number<input type="text" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" /></label><label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" /></label>{error && <div className="alert alert-danger mb-0">{error}</div>}<button className="btn deletion-primary-action w-100" type="submit" disabled={saving}>{saving ? "Signing in…" : `Sign in as ${role === "driver" ? "driver" : "customer"}`}</button></form><div className="deletion-info"><i className="bi bi-info-circle" /> No deletion request can be submitted without account authentication.</div></section><PublicSiteFooter /></main>;
};

export default function AccountDeletion() {
  const location = useLocation();
  const requestedRole = roleFromLoginQuery(location.search);
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(getStoredAuthToken()) && allowedDeletionRoles.includes(storedRole()));
  const token = getStoredAuthToken();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const loadStatus = async () => {
    try {
      setLoading(true);
      setError("");
      setStatus(await getAccountDeletionRequest());
    } catch (requestError) {
      setError(
        requestError.message || "Unable to load deletion request status.",
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isAuthenticated && token) loadStatus();
  }, [isAuthenticated, token]);
  if (!isAuthenticated || !token) return <SignInGate initialRole={requestedRole} onSignedIn={() => setIsAuthenticated(true)} />;
  const requestDeletion = async () => {
    if (!await confirmAction({ title: "Request account deletion?", message: "Your account will be permanently deleted in 90 days. You can cancel this request before the scheduled date.", confirmLabel: "Request deletion", destructive: true })) return;
    try {
      setSaving(true);
      setError("");
      const next = await requestAccountDeletion();
      setStatus(next);
      setNotice("Your account deletion request has been scheduled.");
    } catch (requestError) {
      setError(requestError.message || "Unable to request account deletion.");
    } finally {
      setSaving(false);
    }
  };
  const cancelDeletion = async () => {
    if (!await confirmAction({ title: "Cancel deletion request?", message: "Your account will remain active and the scheduled deletion will be cancelled.", confirmLabel: "Cancel request" })) return;
    try {
      setSaving(true);
      setError("");
      const next = await cancelAccountDeletion();
      setStatus(next);
      setNotice("Your account deletion request has been cancelled.");
    } catch (requestError) {
      setError(requestError.message || "Unable to cancel account deletion.");
    } finally {
      setSaving(false);
    }
  };
  const switchAccount = () => {
    clearAuthSession();
    setStatus(null);
    setError("");
    setNotice("");
    setIsAuthenticated(false);
  };
  const pending = Boolean(status?.deletionRequested);
  return (
    <main className="deletion-page">
      <PublicSiteHeader active="deletion" />
      <section className="deletion-card">
        <div className="deletion-card-top"><div className="deletion-icon"><i className="bi bi-shield-exclamation" /></div><span className="deletion-badge">Secure account request</span></div>
        <p className="legal-kicker">Account settings</p>
        <h1>
          {pending ? "Account deletion scheduled" : "Delete your account"}
        </h1>
        {loading ? (
          <p className="text-muted">Checking your account status…</p>
        ) : pending ? (
          <>
            <p>
              Your account remains available until{" "}
              <strong>{dateText(status.deletionScheduledFor)}</strong>. After
              that date it will be permanently deleted and cannot be recovered.
            </p>
            <div className="deletion-timeline">
              <div>
                <span>Requested</span>
                <strong>{dateText(status.deletionRequestedAt)}</strong>
              </div>
              <div>
                <span>Deletion date</span>
                <strong>{dateText(status.deletionScheduledFor)}</strong>
              </div>
              <div>
                <span>Time remaining</span>
                <strong>{status.daysRemaining ?? "—"} days</strong>
              </div>
            </div>
            <button
              type="button"
              className="btn deletion-secondary-action w-100"
              onClick={cancelDeletion}
              disabled={saving}
            >
              {saving ? "Cancelling…" : "Cancel deletion request"}
            </button>
          </>
        ) : (
          <>
            <p>
              Deletion takes effect after <strong>90 days</strong>. Your account
              remains available during this period. At the end of 90 days, it is
              permanently deleted and cannot be recovered.
            </p>
            <div className="deletion-info">
              <i className="bi bi-info-circle" /> You can cancel your request
              any time before the scheduled deletion date.
            </div>
            <button
              type="button"
              className="btn deletion-primary-action w-100"
              onClick={requestDeletion}
              disabled={saving}
            >
              {saving ? "Requesting…" : "Request account deletion"}
            </button>
          </>
        )}
        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        {notice && (
          <div className="alert alert-success mt-3 mb-0">{notice}</div>
        )}
        <button
          type="button"
          className="btn deletion-switch-account"
          onClick={switchAccount}
          disabled={saving}
        >
          Use a different account
        </button>
      </section>
      <PublicSiteFooter />
    </main>
  );
}
